import { useEffect, useState } from "react";
import 'chart.js/auto';
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import {
    FaIdCard,
    FaMoneyBillWave,
    FaUsers,
    FaGift,
    FaPlusCircle,
    FaMinusCircle,
    FaWallet,
} from "react-icons/fa";

import {
    fetchLoyaltySummary,
    fetchMonthlyPoints,
    fetchMonthlyRevenue,
} from "../../Api/Api";
import LoyaltyCardList from "./LoyaltyCardList";
import { useNotification } from "../Notification/NotificationContext";

Chart.register(...registerables);

export default function LoyaltyDashboard() {
    const [summary, setSummary] = useState(null);
    const [monthlyRevenue, setMonthlyRevenue] = useState([]);
    const [monthlyPoints, setMonthlyPoints] = useState([]);
    const { showNotification } = useNotification();

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            const resp = await fetchLoyaltySummary();
            setSummary(resp.data);

            const rev = await fetchMonthlyRevenue(12);
            setMonthlyRevenue(rev.data);

            const pts = await fetchMonthlyPoints(12);
            setMonthlyPoints(pts.data);
        } catch (err) {
            showNotification(err.message || "Failed to load dashboard", "error");
        }
    };

    // Monthly revenue chart data
    const revLabels = monthlyRevenue.map((m) => m.month);
    const revValues = monthlyRevenue.map((m) => parseFloat(m.value));

    // Monthly points chart data
    const ptsLabels = monthlyPoints.map((m) => m.month);
    const ptsIssued = monthlyPoints.map((m) => parseFloat(m.pointsIssued));
    const ptsRedeemed = monthlyPoints.map((m) => parseFloat(m.pointsRedeemed));

    // KPI mapping with icons + colors
    const kpis = summary
        ? [
            {
                title: "Cards Sold",
                value: summary.cardsSold,
                icon: <FaIdCard className="text-teal-500 text-2xl" />,
            },
            {
                title: "Revenue (₹)",
                value: `₹ ${summary.revenueFromSales.toLocaleString()}`,
                icon: <FaMoneyBillWave className="text-green-500 text-2xl" />,
            },
            {
                title: "Active Members",
                value: summary.activeMembers,
                icon: <FaUsers className="text-blue-500 text-2xl" />,
            },
            {
                title: "Waived Services",
                value: summary.totalWaivedServices,
                icon: <FaGift className="text-purple-500 text-2xl" />,
            },
            {
                title: "Points Issued",
                value: summary.pointsIssued,
                icon: <FaPlusCircle className="text-indigo-500 text-2xl" />,
            },
            {
                title: "Points Redeemed",
                value: summary.pointsRedeemed,
                icon: <FaMinusCircle className="text-red-500 text-2xl" />,
            },
            {
                title: "Points Outstanding",
                value: summary.pointsOutstanding,
                icon: <FaWallet className="text-yellow-500 text-2xl" />,
            },
        ]
        : [];

    return (
        <div className="max-w-screen-xl mx-auto mt-6">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                {/* Header */}
                <div className="bg-teal-500 text-white p-4 rounded-t-lg">
                    <h2 className="text-2xl font-semibold text-center">
                        Loyalty Program Dashboard
                    </h2>
                </div>

                {/* KPI Cards */}
                {summary && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {kpis.map((kpi, index) => (
                            <div
                                key={index}
                                className="bg-white shadow-md p-4 rounded-lg flex items-center"
                            >
                                {kpi.icon}
                                <div className="ml-3">
                                    <p className="text-gray-600 text-sm">{kpi.title}</p>
                                    <p className="text-lg font-semibold text-teal-600">
                                        {kpi.value}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Charts Section */}
                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Revenue Chart */}
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <h3 className="text-sm font-semibold mb-2 text-center">
                            Monthly Revenue (last 12 months)
                        </h3>
                        <Bar
                            data={{
                                labels: revLabels,
                                datasets: [
                                    {
                                        label: "Revenue (₹)",
                                        data: revValues,
                                        backgroundColor: "#2196F3",
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                plugins: { legend: { display: true } },
                                scales: {
                                    y: { beginAtZero: true },
                                },
                            }}
                        />
                    </div>

                    {/* Points Chart */}
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <h3 className="text-sm font-semibold mb-2 text-center">
                            Monthly Points Issued vs Redeemed
                        </h3>
                        <Bar
                            data={{
                                labels: ptsLabels,
                                datasets: [
                                    {
                                        label: "Issued",
                                        data: ptsIssued,
                                        backgroundColor: "#4CAF50",
                                    },
                                    {
                                        label: "Redeemed",
                                        data: ptsRedeemed,
                                        backgroundColor: "#F44336",
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                plugins: { legend: { position: "top" } },
                                scales: {
                                    y: { beginAtZero: true },
                                },
                            }}
                        />
                    </div>
                </div>

                {/* Loyalty Card List (Paginated) */}
                <LoyaltyCardList />
            </div>
        </div>
    );
}
